import React, { useState, useEffect, useMemo } from 'react';
import { calculateDvpRanks, getTeamLogo, normalizeTeamName } from '../utils/teamUtils';

const DefenseVsPosition = ({ players, isDarkMode }) => {
  // Consolidated state with useReducer pattern
  const [state, setState] = useState({
    year: '2025',
    loading: true,
    statData: {},
    rawGamesData: {},
    selectedTab: 'tries',
    selectedFilter: 'All',
    sortColumn: 'team',
    sortDirection: 'asc',
    expandedCell: null,
    showModal: false,
    teamsList: []
  });

  // Shorthand destructuring for cleaner code
  const {
    year, loading, statData, rawGamesData, selectedTab, selectedFilter,
    sortColumn, sortDirection, expandedCell, showModal, teamsList
  } = state;

  // Constants
  const positionGroups = {
    'Spine': ['Fullback', 'Halfback', 'Five-Eighth', 'Hooker'],
    'Backs': ['Centre', 'Winger'],
    'Forwards': ['Prop', '2nd Row', 'Lock'],
    'Bench': ['Interchange', 'Replacement']
  };
  
  const allPositions = [].concat(...Object.values(positionGroups));
  
  const statTabs = [
    { id: 'tries', label: 'Tries' },
    { id: 'performance_points', label: 'Player Performance' },
    { id: 'all_run_metres', label: 'Run Metres' },
    { id: 'tackles_made', label: 'Tackles' },
    { id: 'line_breaks', label: 'Line Breaks' },
    { id: 'tackle_breaks', label: 'Tackle Breaks' },
    { id: 'kick_metres', label: 'Kick Metres' },
    { id: 'offloads', label: 'Offloads' },
    { id: 'try_assists', label: 'Try Assists' },
  ];
  
  const filterOptions = [
    { id: 'L3', label: 'L3' },
    { id: 'L5', label: 'L5' },
    { id: 'L10', label: 'L10' },
    { id: 'All', label: 'All' }
  ];
  
  const years = ['2025', '2024', '2023', '2022', '2021', '2020'];

  // Helper functions
  const extractRoundNumber = (roundString) => {
    const match = roundString.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
  };

  // Simplified update functions
  const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));
  
  const handleCellClick = (team, position) => {
    const hasData = rawGamesData[team]?.[position]?.length > 0;
    if (!hasData) return;

    // Check if mobile
    const isMobile = window.innerWidth < 768;
    
    // Toggle expanded state
    if (expandedCell?.team === team && expandedCell?.position === position) {
      updateState({ expandedCell: null, showModal: false });
    } else {
      updateState({ 
        expandedCell: { team, position },
        showModal: isMobile 
      });
    }
  };

  const handleSort = (position) => {
    const column = position === 'team' ? 'team' : position;
    
    if (sortColumn === column) {
      updateState({ sortDirection: sortDirection === 'asc' ? 'desc' : 'asc' });
    } else {
      updateState({
        sortColumn: column,
        sortDirection: column === 'team' ? 'asc' : 'desc'
      });
    }
  };

  // Calculate performance points once
  const calculatePerformancePoints = (game) => {
    let pp = 0;
    pp += (parseFloat(game['points']) || 0) * 4;
    pp += (parseFloat(game['try_assists']) || 0) * 10;
    pp += Math.floor((parseFloat(game['all_run_metres']) || 0) / 10);
    pp += (parseFloat(game['line_breaks']) || 0) * 5;
    pp += (parseFloat(game['tackles_made']) || 0);
    return pp;
  };

  // Main data processing effect
  useEffect(() => {
    if (!players?.length) return;
    
    updateState({ loading: true });
    
    try {
      // Filter players by year
      const filteredPlayers = players.filter(p => String(p.year) === String(year));
      
      // Get all available rounds for the year
      const availableRounds = [...new Set(filteredPlayers.map(p => extractRoundNumber(p.round)))]
        .sort((a, b) => b - a); // Sort rounds in descending order (most recent first)
      
      // Extract teams
      const uniqueTeams = [...new Set([
        ...filteredPlayers.map(p => p.home_team),
        ...filteredPlayers.map(p => p.away_team)
      ])].filter(Boolean);
      
      // Process stats
      const teamStats = {};
      const rawData = {};
      
      // Determine which rounds to include based on filter
      let roundsToInclude;
      if (selectedFilter === 'L3') {
        roundsToInclude = availableRounds.slice(0, 3);
      } else if (selectedFilter === 'L5') {
        roundsToInclude = availableRounds.slice(0, 5);
      } else if (selectedFilter === 'L10') {
        roundsToInclude = availableRounds.slice(0, 10);
      } else {
        roundsToInclude = availableRounds;
      }
      
      uniqueTeams.forEach(team => {
        teamStats[team] = {};
        rawData[team] = {};
        
        allPositions.forEach(position => {
          // Get games for this team/position within selected rounds
          const games = filteredPlayers.filter(p => {
            const isOpponent = (p.is_home_game && p.away_team === team) || 
                              (!p.is_home_game && p.home_team === team);
            const roundNum = extractRoundNumber(p.round);
            return isOpponent && 
                   p.position === position && 
                   roundsToInclude.includes(roundNum);
          });
          
          // Sort games by round number (most recent first)
          const sortedGames = [...games].sort((a, b) => 
            extractRoundNumber(b.round) - extractRoundNumber(a.round)
          );
          
          // Store raw data
          rawData[team][position] = sortedGames;
          
          // Calculate stats if we have games
          if (sortedGames.length > 0) {
            let total;
            
            if (selectedTab === 'performance_points') {
              total = sortedGames.reduce((sum, game) => 
                sum + calculatePerformancePoints(game), 0);
            } else {
              total = sortedGames.reduce((sum, game) => 
                sum + (parseFloat(game[selectedTab]) || 0), 0);
            }
            
            teamStats[team][position] = {
              average: +(total / sortedGames.length).toFixed(1),
              gamesPlayed: sortedGames.length
            };
          }
        });
      });
      
      updateState({
        statData: teamStats,
        rawGamesData: rawData,
        teamsList: uniqueTeams,
        loading: false
      });
    } catch (error) {
      console.error('Error calculating DVP stats:', error);
      updateState({ loading: false });
    }
  }, [players, year, selectedTab, selectedFilter]);

  // Memoized sorted teams
  const sortedTeams = useMemo(() => {
    if (!sortColumn || !teamsList.length) return teamsList;

    return [...teamsList].sort((teamA, teamB) => {
      // Handle team name sorting
      if (sortColumn === 'team') {
        return sortDirection === 'asc' 
          ? teamA.localeCompare(teamB) 
          : teamB.localeCompare(teamA);
      }
      
      // Handle stat sorting
      const valueA = statData[teamA]?.[sortColumn]?.average || 0;
      const valueB = statData[teamB]?.[sortColumn]?.average || 0;
      
      if (valueA === '-' && valueB === '-') return 0;
      if (valueA === '-') return 1;
      if (valueB === '-') return -1;
      
      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
    });
  }, [teamsList, statData, sortColumn, sortDirection]);

  // UI helper functions
  const isCellExpanded = (team, position) => 
    expandedCell?.team === team && expandedCell?.position === position;

  const getColorClass = (value, position) => {
    // Handle non-numeric values
    if (value === '-' || isNaN(value)) 
      return 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    
    // For expanded view where individual game values are shown
    // We need to compare against averages, not individual game values
    const isIndividualGameValue = expandedCell?.position === position;
    
    // Get values for current position/column only
    const positionValues = Object.values(statData)
      .map(team => team[position]?.average)
      .filter(val => !isNaN(val) && val !== undefined);

    if (positionValues.length === 0) 
      return 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300';

    // Calculate percentiles for this position only
    const sorted = [...positionValues].sort((a, b) => a - b);
    const p25 = sorted[Math.floor(sorted.length * 0.25)] || 0;
    const p50 = sorted[Math.floor(sorted.length * 0.50)] || 0;
    const p75 = sorted[Math.floor(sorted.length * 0.75)] || 0;
    
    // If this is a game value in expanded view, adjust scale
    // Game values are often more extreme than averages
    if (isIndividualGameValue) {
      // Adjust thresholds for individual games
      // (Optional - remove if you want same scale)
      const avg = positionValues.reduce((sum, val) => sum + val, 0) / positionValues.length;
      const stdDev = Math.sqrt(
        positionValues.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / positionValues.length
      );
      
      // For individual games, we allow more extreme values
      const gameP25 = p25 - (0.5 * stdDev);
      const gameP50 = p50;
      const gameP75 = p75 + (0.5 * stdDev);
      
      // Return color class based on adjusted game-specific percentiles
      if (value >= gameP75) return 'bg-green-500 text-white';
      if (value >= gameP50) return 'bg-lime-400 text-black';
      if (value >= gameP25) return 'bg-yellow-400 text-black';
      return 'bg-red-400 text-white';
    }

    // Standard percentile-based coloring for averages
    if (value >= p75) return 'bg-green-500 text-white';
    if (value >= p50) return 'bg-lime-400 text-black';
    if (value >= p25) return 'bg-yellow-400 text-black';
    return 'bg-red-400 text-white';
  };

  // Render modal component
  const GameLogsModal = () => {
    if (!expandedCell || !showModal) return null;

    const { team, position } = expandedCell;
    const games = rawGamesData[team]?.[position] || [];
    const statLabel = statTabs.find(tab => tab.id === selectedTab)?.label || selectedTab;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              {team} vs {position} - Game Logs
            </h3>
            <button 
              onClick={() => updateState({ showModal: false, expandedCell: null })}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="overflow-y-auto max-h-[calc(90vh-8rem)]">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Player</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Team</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Round</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{statLabel}</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {games.map((game, index) => {
                  const statValue = selectedTab === 'performance_points' 
                    ? calculatePerformancePoints(game)
                    : parseFloat(game[selectedTab]) || 0;
                  
                  return (
                    <tr key={index} className="hover:bg-gray-100 dark:hover:bg-gray-700">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">
                        {game.player_name}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        <img src={getTeamLogo(game.player_team)} alt={game.player_team} className="h-4 w-4 inline-block mr-1" />
                        {game.player_team}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {extractRoundNumber(game.round)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-center">
                        <div className={`rounded-full inline-block py-1 px-2 min-w-[40px] font-medium ${getColorClass(statValue, position)}`}>
                          {statValue}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Main component render
  return (
    <div className="h-full w-full">
      <div className="mb-3">
        <h2 className="text-lg md:text-xl font-bold mb-1 dark:text-white">Defense vs Position (DVP)</h2>
        <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300 mb-2">
          How teams perform against each NRL position. Data shows average statistics allowed per position.
          <span className="ml-1 italic">Click on any cell to view player game logs.</span>
        </p>
        
        {/* Controls */}
        <div className="flex flex-wrap md:flex-nowrap items-center gap-2 mb-3">
          <select
            value={year}
            onChange={e => updateState({ year: e.target.value })}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-xs md:text-sm dark:text-white"
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          
          <div className="flex border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
            {filterOptions.map(option => (
              <button
                key={option.id}
                className={`px-2 py-1 text-xs md:text-sm ${
                  selectedFilter === option.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
                onClick={() => updateState({ selectedFilter: option.id })}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Stat tabs */}
        <div className="mb-3 overflow-x-auto md:overflow-visible">
          <div className="flex min-w-max md:flex-wrap border-b border-gray-300 dark:border-gray-600">
            {statTabs.map(tab => (
              <button
                key={tab.id}
                className={`px-2 py-1 md:px-3 md:py-2 font-medium text-xs md:text-sm whitespace-nowrap transition-colors duration-150 ${
                  selectedTab === tab.id
                    ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                onClick={() => updateState({ selectedTab: tab.id })}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Loading or table content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full md:w-full divide-y divide-gray-200 dark:divide-gray-700 text-[10px] md:text-sm table-fixed">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-20">
                <tr>
                  <th 
                    className="px-1 py-1.5 md:px-3 md:py-3 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-800 z-10 w-[90px] md:w-24 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('team')}
                  >
                    <div className="flex items-center">
                      OPPONENT
                      {sortColumn === 'team' && (
                        <span className="ml-1">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  {allPositions.map(position => (
                    <th 
                      key={position} 
                      className="px-1 py-1.5 md:px-3 md:py-3 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap md:w-24 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort(position)}
                    >
                      <div className="flex items-center justify-center">
                        {position}
                        {sortColumn === position && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedTeams.map(team => {
                  // Check if this team has any expanded cell
                  const hasExpandedCell = allPositions.some(pos => isCellExpanded(team, pos));
                  
                  return (
                    <React.Fragment key={team}>
                      {/* Main team row */}
                      <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 ${hasExpandedCell ? 'bg-gray-50 dark:bg-gray-750' : ''}`}>
                        <td className="px-1 py-1.5 md:px-3 md:py-3 whitespace-nowrap text-[10px] md:text-sm font-medium text-gray-800 dark:text-gray-200 sticky left-0 bg-inherit z-10 w-[90px] md:w-24">
                          <div className="flex items-center">
                            <img 
                              src={getTeamLogo(team)} 
                              alt={team} 
                              className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2"
                              onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/16?text=NRL'; }}
                            />
                            <span className="truncate max-w-[65px] md:max-w-full">{normalizeTeamName(team)}</span>
                          </div>
                        </td>
                        {allPositions.map(position => {
                          const data = statData[team]?.[position];
                          const value = data?.average || '-';
                          const isActive = isCellExpanded(team, position);
                          const hasData = rawGamesData[team]?.[position]?.length > 0;
                          
                          return (
                            <td 
                              key={position}
                              onClick={() => hasData && handleCellClick(team, position)}
                              className="px-1 py-1 md:px-2 md:py-2 text-center text-[10px] md:text-sm cursor-pointer transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <div className={`rounded-full inline-block py-0.5 px-1.5 md:py-1 md:px-2 min-w-[32px] md:min-w-[40px] font-medium
                                ${getColorClass(value, position)} ${isActive ? 'ring-2 ring-blue-500' : ''}`}
                              >
                                {value}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                      
                      {/* Expanded game logs row */}
                      {hasExpandedCell && (
                        <tr className="bg-gray-50 dark:bg-gray-750">
                          <td colSpan={allPositions.length + 1} className="p-0">
                            {allPositions.map(position => {
                              if (!isCellExpanded(team, position)) return null;
                              
                              const games = rawGamesData[team]?.[position] || [];
                              if (games.length === 0) return null;
                              
                              return (
                                <div key={position} className="p-2 md:p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                                  <div className="flex justify-between items-center mb-1 md:mb-3">
                                    <h3 className="text-[10px] md:text-sm font-medium text-gray-900 dark:text-white flex items-center">
                                      <img 
                                        src={getTeamLogo(team)} 
                                        alt={team} 
                                        className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2"
                                      />
                                      <span className="font-semibold">{team} vs {position}</span> - Game Logs
                                    </h3>
                                    <button 
                                      onClick={() => updateState({ expandedCell: null })}
                                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                    >
                                      <svg className="h-3 w-3 md:h-4 md:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                  
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-[10px] md:text-sm">
                                      <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr>
                                          <th className="px-1 py-1 md:px-3 md:py-2 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Player
                                          </th>
                                          <th className="px-1 py-1 md:px-3 md:py-2 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Team
                                          </th>
                                          <th className="px-1 py-1 md:px-3 md:py-2 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Round
                                          </th>
                                          <th className="px-1 py-1 md:px-3 md:py-2 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            {statTabs.find(tab => tab.id === selectedTab)?.label || selectedTab}
                                          </th>
                                          <th className="hidden md:table-cell px-1 py-1 md:px-3 md:py-2 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Venue
                                          </th>
                                          <th className="hidden md:table-cell px-1 py-1 md:px-3 md:py-2 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                            Score
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {games.map((game, index) => {
                                          let statValue;
                                          
                                          if (selectedTab === 'performance_points') {
                                            // Calculate PP for this game
                                            let pp = 0;
                                            // 4pts per point scored
                                            pp += (parseFloat(game['points']) || 0) * 4;
                                            // 10pts per try assist
                                            pp += (parseFloat(game['try_assists']) || 0) * 10;
                                            // 1pt per 10-meter run
                                            const runMeters = parseFloat(game['all_run_metres']) || 0;
                                            pp += Math.floor(runMeters / 10);
                                            // 5pts per line break
                                            pp += (parseFloat(game['line_breaks']) || 0) * 5;
                                            // 1pt per tackle
                                            pp += (parseFloat(game['tackles_made']) || 0);
                                            statValue = pp;
                                          } else {
                                            statValue = parseFloat(game[selectedTab]) || 0;
                                          }
                                          
                                          return (
                                            <tr key={index} className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150">
                                              <td className="px-1 py-1 md:px-3 md:py-2 whitespace-nowrap text-[10px] md:text-sm font-medium text-gray-800 dark:text-gray-200">
                                                {game.player_name}
                                              </td>
                                              <td className="px-1 py-1 md:px-3 md:py-2 whitespace-nowrap text-[10px] md:text-sm text-gray-600 dark:text-gray-300">
                                                <div className="flex items-center">
                                                  <img 
                                                    src={getTeamLogo(game.player_team)} 
                                                    alt={game.player_team} 
                                                    className="hidden md:inline-block h-4 w-4 mr-1"
                                                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/16?text=NRL'; }}
                                                  />
                                                  {game.player_team}
                                                </div>
                                              </td>
                                              <td className="px-1 py-1 md:px-3 md:py-2 whitespace-nowrap text-[10px] md:text-sm text-gray-600 dark:text-gray-300">
                                                {game.round}
                                              </td>
                                              <td className="px-1 py-1 md:px-3 md:py-2 whitespace-nowrap text-[10px] md:text-sm text-center">
                                                <div className={`rounded-full inline-block py-1 px-2 min-w-[40px] font-medium ${getColorClass(statValue, position)}`}>
                                                  {statValue}
                                                </div>
                                              </td>
                                              <td className="hidden md:table-cell px-1 py-1 md:px-3 md:py-2 whitespace-nowrap text-[10px] md:text-sm text-gray-600 dark:text-gray-300">
                                                {game.venue || '-'}
                                              </td>
                                              <td className="hidden md:table-cell px-1 py-1 md:px-3 md:py-2 whitespace-nowrap text-[10px] md:text-sm text-center text-gray-600 dark:text-gray-300">
                                                {game.score || '-'}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            })}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Modal */}
      <GameLogsModal />
    </div>
  );
};

export default DefenseVsPosition; 